import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StickyNote, Plus, Check, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { mdyTime } from "@/lib/dateFmt";
import { toast } from "sonner";

export default function NotesPanel({ payPeriodId, canEdit, employees, facilityId }) {
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState("");
  const [empId, setEmpId] = useState("all");
  const [approved, setApproved] = useState(true);

  const load = async () => {
    if (!payPeriodId || !facilityId) return;
    try {
      const { data } = await api.get(`/notes/${payPeriodId}?facility_id=${facilityId}`);
      setNotes(data);
    } catch { setNotes([]); }
  };
  useEffect(() => { load(); }, [payPeriodId, facilityId]);

  const create = async () => {
    if (!content.trim()) return;
    try {
      await api.post("/notes", {
        pay_period_id: payPeriodId,
        facility_id: facilityId,
        employee_id: empId === "all" ? null : empId,
        content,
        approved,
      });
      setContent(""); load(); toast.success("Note posted");
    } catch { toast.error("Failed"); }
  };
  const approve = async (id) => { await api.patch(`/notes/${id}/approve`); load(); };
  const remove  = async (id) => { await api.delete(`/notes/${id}`); load(); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="notes-panel">
      <div className="flex items-center gap-2 text-lg font-display font-semibold mb-1">
        <StickyNote size={18} className="text-blue-600" /> Notes
      </div>
      <div className="text-sm text-slate-500 mb-4">
        {canEdit ? "Post schedule notes. Approved notes are visible to employees." : "Approved notes for this pay period."}
      </div>

      {canEdit && (
        <div className="border border-slate-200 rounded-xl p-3 mb-4 space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Visible to</Label>
              <Select value={empId} onValueChange={setEmpId}>
                <SelectTrigger data-testid="note-employee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 mt-6">
              <Checkbox id="approved" checked={approved} onCheckedChange={setApproved} data-testid="note-approved" />
              <Label htmlFor="approved" className="text-sm">Publish immediately (approved)</Label>
            </div>
          </div>
          <Textarea
            value={content} onChange={e => setContent(e.target.value)}
            placeholder="Reminder, coverage note, kudos…"
            data-testid="note-content"
          />
          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={create} data-testid="note-post">
              <Plus size={14} className="mr-1" /> Post note
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {notes.map(n => (
          <div key={n.id} className="border border-slate-200 rounded-xl p-3" data-testid={`note-${n.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {n.created_by_name} · {mdyTime(n.created_at)}
                {n.employee_id ? " · Personal" : " · Team"}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${n.approved ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {n.approved ? "Approved" : "Draft"}
                </span>
                {canEdit && !n.approved && (
                  <button onClick={() => approve(n.id)} className="text-emerald-700 hover:underline text-xs flex items-center gap-1" data-testid={`note-approve-${n.id}`}>
                    <Check size={12} /> Approve
                  </button>
                )}
                {canEdit && (
                  <button onClick={() => remove(n.id)} className="text-red-600 hover:underline text-xs flex items-center gap-1" data-testid={`note-delete-${n.id}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-800 whitespace-pre-wrap">{n.content}</div>
          </div>
        ))}
        {notes.length === 0 && <div className="text-sm text-slate-500">No notes yet.</div>}
      </div>
    </div>
  );
}
